export interface TocExpandState {
  expandedIds: Ref<Set<string>>
  expandableIds: ComputedRef<Set<string>>
  allExpanded: ComputedRef<boolean>
  expandAll: () => void
  collapseAll: () => void
  isExpanded: (id: string) => boolean
  setExpanded: (id: string, expanded: boolean) => void
}

export function collectExpandableIds<T>(tree: T[], getChildren: (node: T) => T[], getId: (node: T) => string): Set<string> {
  const ids = new Set<string>()

  function walk(nodes: T[]) {
    for (const node of nodes) {
      const children = getChildren(node)
      if (children.length > 0) {
        ids.add(getId(node))
        walk(children)
      }
    }
  }

  walk(tree)
  return ids
}

export function createTocExpandState<T>(tree?: MaybeRefOrGetter<T[]>, getChildren?: (node: T) => T[], getId?: (node: T) => string): TocExpandState {
  const expandedIds = ref(new Set<string>())

  const expandableIds = computed(() => {
    if (!tree || !getChildren || !getId) {
      return new Set<string>()
    }
    return collectExpandableIds(toValue(tree), getChildren, getId)
  })

  const allExpanded = computed(() => {
    const expandable = expandableIds.value
    return expandable.size > 0 && [...expandable].every(id => expandedIds.value.has(id))
  })

  function isExpanded(id: string) {
    return expandedIds.value.has(id)
  }

  function setExpanded(id: string, expanded: boolean) {
    if (expanded === expandedIds.value.has(id)) {
      return
    }

    const next = new Set(expandedIds.value)
    if (expanded) {
      next.add(id)
    }
    else {
      next.delete(id)
    }
    expandedIds.value = next
  }

  function expandAll() {
    expandedIds.value = new Set(expandableIds.value)
  }

  function collapseAll() {
    expandedIds.value = new Set()
  }

  return {
    expandedIds,
    expandableIds,
    allExpanded,
    expandAll,
    collapseAll,
    isExpanded,
    setExpanded,
  }
}

export const TocExpandContextKey: InjectionKey<TocExpandState> = Symbol('TocExpandContext')

export function useTocExpandProvider<T>(tree?: MaybeRefOrGetter<T[]>, getChildren?: (node: T) => T[], getId?: (node: T) => string) {
  const state = createTocExpandState(tree, getChildren, getId)
  provide(TocExpandContextKey, state)
  return state
}

function injectTocExpandState(): TocExpandState {
  return inject(TocExpandContextKey, null) ?? createTocExpandState()
}

export function useTocExpandConsumer(): TocExpandState {
  return injectTocExpandState()
}

export function useTocExpandController(): TocExpandState {
  return injectTocExpandState()
}
